import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { sendFranchiseContactFormSubmitEvent } from "tag-manager";

import { useMd } from "~franchise-website/hooks/index";
import { useSetIsPipedriveSuccess } from "~franchise-website/states";
import { addLead, addNote, addOrganization, addPerson } from "./pipedrive";

const validationSchema = yup.object({
	name: yup
		.string()
		.trim()
		.min(2, "Please enter a valid name")
		.max(50, "Please enter a valid name")
		.required("Please specify your first name"),
	email: yup
		.string()
		.trim()
		.email("Please enter a valid email address")
		.required("Email is required."),
});

const ContactForm = () => {
	const initialValues = {
		name: "",
		lastName: "",
		email: "",
		franchiseBrand: "",
		locations: "0",
		franchiseGoals: 0,
		registeredFranchise: false,
	};

	const isMd = useMd();

	const [loading, setLoading] = useState(false);
	const setIsPipedriveSuccess = useSetIsPipedriveSuccess();

	const onSubmit = async () => {
		setLoading(true);
		const { data: organisation } = await addOrganization(formik.values);
		const { data: person } = await addPerson(formik.values, organisation.id);
		const { data: lead } = await addLead(
			formik.values,
			organisation.id,
			person.id,
		);
		await addNote(formik.values, organisation.id, person.id, lead.id);

		sendFranchiseContactFormSubmitEvent();

		setIsPipedriveSuccess(true);
		setLoading(false);
	};

	const formik = useFormik({
		initialValues,
		validationSchema: validationSchema,
		onSubmit,
	});

	return (
		<Box>
			<form onSubmit={formik.handleSubmit}>
				<Grid
					container
					spacing={4}>
					<Grid
						item
						xs={12}
						sm={12}>
						<Typography
							variant={"subtitle2"}
							sx={{ marginBottom: 2 }}>
							Your name
						</Typography>
						<TextField
							label="Your name *"
							variant="outlined"
							name={"name"}
							fullWidth
							value={formik.values.name}
							onChange={formik.handleChange}
							error={formik.touched.name && Boolean(formik.errors.name)}
							helperText={formik.touched.name && formik.errors.name}
						/>
					</Grid>
					<Grid
						item
						xs={12}>
						<Typography
							variant={"subtitle2"}
							sx={{ marginBottom: 2 }}>
							Enter your email
						</Typography>
						<TextField
							label="Work email *"
							variant="outlined"
							name={"email"}
							placeholder="Enter your work email"
							fullWidth
							value={formik.values.email}
							onChange={formik.handleChange}
							error={formik.touched.email && Boolean(formik.errors.email)}
							helperText={formik.touched.email && formik.errors.email}
						/>
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}>
						<Typography
							variant={"subtitle2"}
							sx={{ marginBottom: 2 }}>
							Franchise brand
						</Typography>
						<TextField
							label="Franchise Brand"
							variant="outlined"
							name={"franchiseBrand"}
							fullWidth
							value={formik.values.franchiseBrand}
							onChange={formik.handleChange}
						/>
					</Grid>
					<Grid
						item
						xs={12}>
						<Typography
							variant={"subtitle2"}
							sx={{ marginBottom: 2 }}>
							How many open locations?
						</Typography>
						<TextField
							select
							label="Locations"
							variant="outlined"
							name={"locations"}
							fullWidth
							value={formik.values.locations}
							onChange={formik.handleChange}>
							{["0", "1-10", "10-50", "50+"].map((option) => (
								<MenuItem
									key={option}
									value={option}>
									{option}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid
						item
						xs={12}>
						<Typography
							variant={"subtitle2"}
							sx={{ marginBottom: 2 }}>
							Goals for number of franchises in the next year?
						</Typography>
						<TextField
							label="Franchise Goals"
							variant="outlined"
							name={"franchiseGoals"}
							fullWidth
							multiline
							value={formik.values.franchiseGoals}
							onChange={formik.handleChange}
						/>
					</Grid>
					<Grid
						item
						xs={12}>
						<Typography
							variant={"subtitle2"}
							sx={{ marginBottom: 2 }}>
							Are you currently a registered franchise?
						</Typography>
						<RadioGroup
							defaultValue="No"
							name="registeredFranchise"
							onChange={formik.handleChange}>
							<FormControlLabel
								value="Yes"
								control={<Radio />}
								label="Yes"
							/>
							<FormControlLabel
								value="No"
								control={<Radio />}
								label="No"
							/>
						</RadioGroup>
					</Grid>
					<Grid
						item
						container
						xs={12}
						justifyContent={isMd ? "left" : "center"}>
						<Box sx={{ m: 1, position: "relative" }}>
							<Button
								sx={{ width: "200px" }}
								size={"large"}
								variant={"contained"}
								type={"submit"}
								disabled={loading}>
								Send
							</Button>
							{loading && (
								<CircularProgress
									size={24}
									sx={{
										position: "absolute",
										top: "50%",
										left: "50%",
										marginTop: "-12px",
										marginLeft: "-12px",
									}}
								/>
							)}
						</Box>
					</Grid>
				</Grid>
			</form>
		</Box>
	);
};

export default ContactForm;
